interface LogoProps {
  variant?: 'horizontal' | 'square';
  scrolled?: boolean;
}

export default function Logo({ variant = 'horizontal', scrolled = false }: LogoProps) {
  if (variant === 'square') {
    return (
      <div className="w-12 h-12 rounded-2xl overflow-hidden flex-shrink-0" style={{
        background: 'linear-gradient(135deg, #E91E8C 0%, #D946A6 40%, #3D1A6E 100%)',
        boxShadow: '0 4px 12px rgba(233,30,140,0.4)'
      }}>
        <div className="w-full h-full flex flex-col items-center justify-center relative">
          {/* Sun rays */}
          <div className="absolute top-1 left-1/2 -translate-x-1/2">
            <div className="flex items-center justify-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="w-0.5 h-1.5 bg-[#F5C518] rounded-full opacity-90" style={{ transform: `rotate(${(i - 2) * 20}deg)`, transformOrigin: 'bottom center' }}></div>
              ))}
            </div>
          </div>
          {/* Sun circle */}
          <div className="w-4 h-4 rounded-full bg-[#F5C518] shadow-sm mb-0.5 mt-2 flex-shrink-0"></div>
          {/* Tent */}
          <div className="relative flex items-end justify-center" style={{ width: 22, height: 14 }}>
            {/* Tent body */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2" style={{
              width: 0,
              height: 0,
              borderLeft: '11px solid transparent',
              borderRight: '11px solid transparent',
              borderBottom: '14px solid #F5C518',
            }}></div>
            {/* Tent door */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2.5 h-3 rounded-t-full bg-[#3D1A6E] opacity-80"></div>
          </div>
          {/* Ground line */}
          <div className="w-8 h-0.5 bg-white/40 rounded-full mt-0.5"></div>
          {/* Trees */}
          <div className="absolute bottom-1 left-1 flex flex-col items-center">
            <div style={{ width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderBottom: '7px solid rgba(255,255,255,0.5)' }}></div>
            <div className="w-1 h-1.5 bg-white/40 rounded-sm"></div>
          </div>
          <div className="absolute bottom-1 right-1 flex flex-col items-center">
            <div style={{ width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderBottom: '7px solid rgba(255,255,255,0.5)' }}></div>
            <div className="w-1 h-1.5 bg-white/40 rounded-sm"></div>
          </div>
        </div>
      </div>
    );
  }

  // Horizontal variant
  return (
    <div className="flex items-center gap-3">
      {/* Icon mark */}
      <div className="w-11 h-11 rounded-2xl overflow-hidden flex-shrink-0 relative" style={{
        background: 'linear-gradient(135deg, #E91E8C 0%, #D946A6 40%, #3D1A6E 100%)',
        boxShadow: '0 4px 14px rgba(233,30,140,0.35)'
      }}>
        <div className="w-full h-full flex flex-col items-center justify-center relative">
          {/* Sun rays */}
          <div className="absolute top-0.5 left-1/2 -translate-x-1/2 flex items-end justify-center gap-0.5">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="w-0.5 h-1.5 bg-[#F5C518] rounded-full opacity-90" style={{ transform: `rotate(${(i - 2) * 20}deg)`, transformOrigin: 'bottom center' }}></div>
            ))}
          </div>
          {/* Sun */}
          <div className="w-3.5 h-3.5 rounded-full bg-[#F5C518] mt-2 mb-0.5 flex-shrink-0 shadow-sm"></div>
          {/* Tent */}
          <div className="relative flex items-end justify-center" style={{ width: 20, height: 12 }}>
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2" style={{
              width: 0, height: 0,
              borderLeft: '10px solid transparent',
              borderRight: '10px solid transparent',
              borderBottom: '12px solid #F5C518',
            }}></div>
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2.5 rounded-t-full bg-[#3D1A6E] opacity-80"></div>
          </div>
          <div className="w-7 h-0.5 bg-white/40 rounded-full mt-0.5"></div>
          {/* Trees */}
          <div className="absolute bottom-1 left-0.5 flex flex-col items-center">
            <div style={{ width: 0, height: 0, borderLeft: '3px solid transparent', borderRight: '3px solid transparent', borderBottom: '6px solid rgba(255,255,255,0.5)' }}></div>
            <div className="w-1 h-1 bg-white/40 rounded-sm"></div>
          </div>
          <div className="absolute bottom-1 right-0.5 flex flex-col items-center">
            <div style={{ width: 0, height: 0, borderLeft: '3px solid transparent', borderRight: '3px solid transparent', borderBottom: '6px solid rgba(255,255,255,0.5)' }}></div>
            <div className="w-1 h-1 bg-white/40 rounded-sm"></div>
          </div>
        </div>
      </div>

      {/* Text lockup */}
      <div className="flex flex-col leading-tight">
        <div
          className="font-black leading-none tracking-tight"
          style={{
            fontFamily: 'Fredoka, Poppins, system-ui, sans-serif',
            fontSize: '1.15rem',
            background: scrolled
              ? 'linear-gradient(90deg, #E91E8C 0%, #3D1A6E 100%)'
              : 'linear-gradient(90deg, #ffffff 0%, #FDE68A 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          The Mommy Mingle
        </div>
        <div
          className="font-bold leading-none tracking-wide"
          style={{
            fontFamily: 'Fredoka, Poppins, system-ui, sans-serif',
            fontSize: '0.72rem',
            color: scrolled ? '#F5C518' : 'rgba(255,255,255,0.92)',
            letterSpacing: '0.04em',
          }}
        >
          ☀ Summer Camp Guide
        </div>
        <div
          className="leading-none mt-0.5"
          style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontSize: '0.58rem',
            color: scrolled ? '#9B59B6' : 'rgba(255,255,255,0.65)',
            letterSpacing: '0.02em',
          }}
        >
          A Community Resource by The Mommy Mingle
        </div>
      </div>
    </div>
  );
}
